import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import { useEffect, useState } from "react";
import appCssUrl from "../styles.css?url";
import { SITE } from "@/lib/routes";
import { websiteJsonLd, organizationJsonLd } from "@/lib/seo";

// Client-only extras that are never needed for first paint: the toast host
// (fires only on user actions), the PWA manager (registers the service
// worker + install/update prompts, all inside effects) and the GA4 loader
// (which itself waits for interaction or idle before fetching gtag.js).
// Their code is imported after hydration so it stays out of the initial
// bundle. No React.lazy/Suspense: a Suspense fallback shown right after
// hydration makes React hold back the page's own hydration for its ~300 ms
// reveal throttle, and the Mediavine script inserts ads in that window
// (React error #418).
type Extras = {
  Toaster: typeof import("@/components/ui/sonner").Toaster;
  PwaManager: typeof import("@/components/pwa/PwaManager").PwaManager;
  Analytics: typeof import("@/components/analytics/Analytics").Analytics;
};

function DeferredClientExtras() {
  const [extras, setExtras] = useState<Extras | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all([
      import("@/components/ui/sonner"),
      import("@/components/pwa/PwaManager"),
      import("@/components/analytics/Analytics"),
    ]).then(([sonner, pwa, analytics]) => {
      if (live)
        setExtras({
          Toaster: sonner.Toaster,
          PwaManager: pwa.PwaManager,
          Analytics: analytics.Analytics,
        });
    });
    return () => {
      live = false;
    };
  }, []);
  if (!extras) return null;
  const { Toaster, PwaManager, Analytics } = extras;
  return (
    <>
      <Toaster
        position="top-center"
        richColors
        // Just below the sticky header (h-16 + border). The bottom of the
        // screen belongs to Mediavine: the adhesion ad and, bottom right, the
        // Universal Player.
        offset={{ top: "4.75rem" }}
        mobileOffset={{ top: "4.75rem" }}
      />
      <PwaManager />
      <Analytics />
    </>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { title: "ReadBarcode.com — Online Barcode Reader, Lookup & GS1 Tools" },
      {
        name: "description",
        content:
          "Free online barcode reader, UPC/EAN/GTIN lookup, GS1 Digital Link decoder, QR and Data Matrix scanner — all in your browser.",
      },
      { name: "author", content: "ReadBarcode" },
      // PWA / theming: tells iOS, Android and desktop browsers how to render
      // the address bar, the home-screen icon and the splash background when
      // the site is launched as an installed app.
      { name: "theme-color", content: "#ffffff", media: "(prefers-color-scheme: light)" },
      { name: "theme-color", content: "#0b1120", media: "(prefers-color-scheme: dark)" },
      { name: "application-name", content: "ReadBarcode" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "ReadBarcode" },
      { name: "format-detection", content: "telephone=no" },
      { name: "msapplication-TileColor", content: "#2c5cf5" },
      { name: "msapplication-tap-highlight", content: "no" },
      { property: "og:site_name", content: `${SITE.name}.com` },
      { property: "og:title", content: "ReadBarcode.com — Online Barcode Reader" },
      {
        property: "og:description",
        content:
          "Scan, read, validate and decode barcodes online. UPC, EAN, GTIN, ISBN, QR, Data Matrix and GS1 Digital Link.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@readbarcode" },
    ],
    links: [
      // In production the CSS is inlined into <head> at the edge (see
      // src/server.ts, HTMLRewriter) so it is not a render-blocking request.
      // The dev server has no such transform, so keep a stylesheet link there.
      ...(import.meta.env.DEV ? [{ rel: "stylesheet" as const, href: appCssUrl }] : []),
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "manifest", href: "/site.webmanifest" },
      { rel: "alternate", type: "application/xml", href: "/sitemap.xml", title: "Sitemap" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(websiteJsonLd()),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(organizationJsonLd()),
      },
      // Mediavine (Journey) ad script, exactly as supplied in the Mediavine
      // dashboard. Journey's docs require it in the server-rendered <head>:
      // its crawler reads the HTML to confirm the install. Hydration must
      // finish before Journey inserts ads, which src/client.tsx ensures.
      {
        type: "text/javascript",
        async: true,
        "data-noptimize": "1",
        "data-cfasync": "false",
        src: "//scripts.scriptwrapper.com/tags/d4ebf620-c071-4e6b-b4fa-10f726566839.js",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  useFullPageNavigation();
  return (
    <>
      <Outlet />
      <DeferredClientExtras />
    </>
  );
}

// Every internal link loads a full page instead of a client-side route
// change, so each page view gets a fresh Mediavine ad load and is counted
// once (ads are only requested on page load).
function useFullPageNavigation() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin) return;
      // Same-page anchors (#section) keep their normal in-page jump.
      if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
      e.preventDefault();
      e.stopPropagation();
      location.assign(url.href);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}
